import type {
  Area,
  Board,
  EpicSummary,
  Status,
  TicketCard,
  ViewSettings,
} from "@beacon/core/projects"

/** A Board's columns from the left, each Status with its cards from the top. */
export function columnsOf(board: Board): { status: Status; cards: TicketCard[] }[] {
  return board.statuses.map((status) => ({
    status,
    cards: board.tickets.filter((card) => card.statusId === status.id),
  }))
}

/**
 * A Board grouped by Epic: a lane per Epic in the order the Board gives them, then a lane for the
 * cards in none, left out while it has no card, since a card drags only within its lane and so could
 * never land in it. Each lane has the Board's columns and its own cards in them, in Board order.
 * `withEmpty: false` leaves out the lanes with no card, as a filtered Board does: a filter for one
 * Epic would otherwise list every other Epic, each with nothing in it.
 */
export function lanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; epic: EpicSummary | null; columns: ReturnType<typeof columnsOf> }[] {
  const lane = (epic: EpicSummary | null) => ({
    id: laneIdOf(epic),
    epic,
    columns: columnsOf({
      ...board,
      tickets: board.tickets.filter((card) => card.epic === (epic?.number ?? null)),
    }),
  })
  const filled = ({ columns }: ReturnType<typeof lane>) => columns.some(({ cards }) => cards.length)
  const lanes = [...board.epics.map(lane), lane(null)]
  return lanes.filter((each) => (withEmpty && each.epic !== null) || filled(each))
}

/** How a viewer's browser names a lane it keeps collapsed: its Epic's number, or `none`. */
export const laneIdOf = (epic: Pick<EpicSummary, "number"> | null) =>
  epic ? String(epic.number) : "none"

/** The lane for cards in no Area, named apart from every Area's id and from the Epic lanes'. */
const noAreaLane = "no-area"

/**
 * A Board grouped by Area: a lane per Area in the Project's order, then a lane for the cards in
 * none, left out while it has no card, each lane with the Board's columns and its own cards in
 * them, in Board order. A lane is named by its Area's id, or `no-area`, which is how the browser
 * keeps it collapsed. `withEmpty: false` leaves out every lane with no card, as a filtered Board
 * does.
 */
export function areaLanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; area: Area | null; columns: ReturnType<typeof columnsOf> }[] {
  const lane = (area: Area | null) => ({
    id: area?.id ?? noAreaLane,
    area,
    columns: columnsOf({
      ...board,
      tickets: board.tickets.filter((card) => card.areaId === (area?.id ?? null)),
    }),
  })
  const filled = ({ columns }: ReturnType<typeof lane>) => columns.some(({ cards }) => cards.length)
  const lanes = [...board.areas.map(lane), lane(null)]
  return lanes.filter((each) => (withEmpty && each.area !== null) || filled(each))
}

/**
 * A Board grouped by Area then Epic: the Area lanes `areaLanesOf` gives, each holding the Epic
 * lanes `lanesOf` gives for its own cards and Epics, a "No Epic" lane last. An Epic with an Area is
 * under that Area alone. An Epic with no Area is under each Area its cards are in, and under "No
 * Area" for its cards in none, or when it has no card on the Board at all, so it still shows
 * somewhere. The lane for cards in no Area is left out while it holds nothing. A nested lane is
 * named by its Area's lane, then its Epic's (`a-pay/7`, `no-area/none`), which is how the browser
 * keeps it collapsed. `withEmpty: false` leaves out every lane with no card, at both levels.
 */
export function areaEpicLanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; area: Area | null; lanes: ReturnType<typeof lanesOf> }[] {
  const carded = new Set(board.tickets.map(({ epic }) => epic))
  const lane = (area: Area | null) => {
    const id = area?.id ?? noAreaLane
    const tickets = board.tickets.filter((card) => card.areaId === (area?.id ?? null))
    const hasCardHere = new Set(tickets.map(({ epic }) => epic))
    const epics = board.epics.filter((epic) =>
      epic.areaId === null
        ? hasCardHere.has(epic.number) || (area === null && !carded.has(epic.number))
        : epic.areaId === area?.id,
    )
    const lanes = lanesOf({ ...board, tickets, epics }, { withEmpty }).map((nested) => ({
      ...nested,
      id: `${id}/${nested.id}`,
    }))
    return { id, area, lanes, epics, filled: tickets.length > 0 }
  }
  return [...board.areas.map(lane), lane(null)]
    .filter(
      ({ area, epics, filled }) => (withEmpty && (area !== null || epics.length > 0)) || filled,
    )
    .map(({ id, area, lanes }) => ({ id, area, lanes }))
}

/** A lane on one Status's page of the compact Board: its Epic's or its Area's, and its cards there. */
export type PageLane =
  | { id: string; epic: EpicSummary | null; cards: TicketCard[] }
  | { id: string; area: Area | null; cards: TicketCard[] }

/** An Area's lane on one Status's page, grouped by Area then Epic: its Epic lanes there. */
export type PageArea = { id: string; area: Area | null; lanes: PageLane[] }

/**
 * One Status's page of the compact Board, which shows a Status at a time: its `cards`, or, grouped,
 * the `lanes` holding a card in it, each with its cards there, in the order and by the ids the
 * Board's lanes have, so a lane folded on one page is folded on every page. Grouped by Area then
 * Epic, its `areas` each hold the Epic lanes with a card in it. A lane with no card in this Status
 * is left out, at every level, so a page lists only what it shows.
 */
export function statusPageOf(
  board: Board,
  grouping: BoardGrouping,
  statusId: string,
): { cards: TicketCard[] } | { lanes: PageLane[] } | { areas: PageArea[] } {
  const inStatus = (columns: ReturnType<typeof columnsOf>) =>
    columns.find(({ status }) => status.id === statusId)?.cards ?? []
  const epicLanes = (lanes: ReturnType<typeof lanesOf>): PageLane[] =>
    lanes
      .map(({ id, epic, columns }) => ({ id, epic, cards: inStatus(columns) }))
      .filter(({ cards }) => cards.length > 0)
  switch (grouping) {
    case "none":
      return { cards: inStatus(columnsOf(board)) }
    case "epic":
      return { lanes: epicLanes(lanesOf(board, { withEmpty: false })) }
    case "area":
      return {
        lanes: areaLanesOf(board, { withEmpty: false })
          .map(({ id, area, columns }) => ({ id, area, cards: inStatus(columns) }))
          .filter(({ cards }) => cards.length > 0),
      }
    case "area-epic":
      return {
        areas: areaEpicLanesOf(board, { withEmpty: false })
          .map(({ id, area, lanes }) => ({ id, area, lanes: epicLanes(lanes) }))
          .filter(({ lanes }) => lanes.length > 0),
      }
  }
}

/** The URL parameter the compact Board keeps the Status it shows in, by its id. */
export const shownStatusParam = "column"

/**
 * The Status the compact Board opens on, by its id: the one its URL names (`column=`), else the
 * one last viewed on this Project in this browser, else its first Started Status, else its first.
 * A Status the Project no longer has is passed over, wherever it was named. None for a Board with
 * no Status.
 */
export function openingStatusOf(
  statuses: readonly Pick<Status, "id" | "category">[],
  { asked, lastViewed }: { asked: string | null; lastViewed: string | null },
): string | undefined {
  const held = (id: string | null) =>
    id !== null && statuses.some((status) => status.id === id) ? id : undefined
  return (
    held(asked) ??
    held(lastViewed) ??
    statuses.find(({ category }) => category === "started")?.id ??
    statuses[0]?.id
  )
}

/** Which lanes to collapse and which to open, as one change to what the browser keeps. */
export type LaneChange = { collapse: string[]; expand: string[] }

/**
 * Folding a grouped Board's lanes all at once, for an overview: `fold` collapses the innermost
 * lanes (the Epic lanes inside Area lanes, or every lane of a Board grouped one level deep) and
 * opens the Area lanes around them, so each shows its Epics' heads and progress; `unfold` opens
 * every lane. `folded` is whether the Board is folded so now, which the toggle offers to undo.
 */
export function laneFoldOf(
  board: Board,
  grouping: Exclude<BoardGrouping, "none">,
  options: { withEmpty: boolean },
  collapsed: ReadonlySet<string>,
): { folded: boolean; fold: LaneChange; unfold: LaneChange } {
  const [outer, innermost] =
    grouping === "area-epic"
      ? (() => {
          const lanes = areaEpicLanesOf(board, options)
          return [
            lanes.map(({ id }) => id),
            lanes.flatMap(({ lanes }) => lanes.map(({ id }) => id)),
          ]
        })()
      : [
          [],
          (grouping === "epic" ? lanesOf(board, options) : areaLanesOf(board, options)).map(
            ({ id }) => id,
          ),
        ]
  return {
    folded:
      innermost.length > 0 &&
      innermost.every((id) => collapsed.has(id)) &&
      !outer.some((id) => collapsed.has(id)),
    fold: { collapse: innermost, expand: outer },
    unfold: { collapse: [], expand: [...outer, ...innermost] },
  }
}

/**
 * The Epic a card is in, as its chip names it: by its key and title from the Board's Epics, or by
 * the key its number makes when the Board doesn't list it (a completed Epic left off). None for a
 * card in no Epic.
 */
export function epicOfCard(
  card: Pick<TicketCard, "epic">,
  projectKey: string,
  epics: readonly Pick<EpicSummary, "number" | "key" | "title">[],
): { key: string; title?: string } | undefined {
  if (card.epic === null) return undefined
  const epic = epics.find(({ number }) => number === card.epic)
  return epic ? { key: epic.key, title: epic.title } : { key: `${projectKey}-${card.epic}` }
}

/** Where the Project's settings list its Statuses, for a column's "Status settings". */
export const statusSettingsOf = (projectKey: string) => `/projects/${projectKey}/settings#statuses`

/** Where a ticket's page is, by its Project's key and its number. */
export function ticketPath(key: string, number: number) {
  return `/projects/${key}/${number}`
}

/** Where a ticket's page is, by its key, such as `WEB-3`; none for what isn't a key. */
export function pathOfKey(key: string) {
  const [, projectKey, number] = /^([A-Z]+)-(\d+)$/.exec(key) ?? []
  return projectKey && number ? ticketPath(projectKey, Number(number)) : undefined
}

/**
 * The filters a Board's URL may name once, each as `projects` reads it. Its Labels it names apart,
 * as a repeated `label`.
 */
export const boardFilters = [
  "assignee",
  "type",
  "priority",
  "size",
  "text",
  "epic",
  "area",
] as const

export type BoardFilter = (typeof boardFilters)[number]

/** The filters a Board's URL names, leaving out any set to nothing. */
export function boardFiltersIn(params: URLSearchParams) {
  const filters: Partial<Record<BoardFilter, string>> = {}
  for (const filter of boardFilters) {
    const value = params.get(filter)
    if (value) filters[filter] = value
  }
  return filters
}

/** A Board's URL with one filter set, or cleared when set to nothing, and the rest kept. */
export function withBoardFilter(
  params: URLSearchParams,
  filter: BoardFilter,
  value: string | undefined,
) {
  const next = new URLSearchParams(params)
  if (value) next.set(filter, value)
  else next.delete(filter)
  return next
}

/** The parameter a Board's URL repeats, once for each Label it is filtered to. */
const labelParam = "label"

/** The Labels a Board's URL filters it to, by id, each once, in the order it names them. */
export function boardLabelsIn(params: URLSearchParams) {
  return [...new Set(params.getAll(labelParam).filter(Boolean))]
}

/**
 * A Board's URL filtered to the Labels named, each in a `label` of its own, or to none, and the
 * rest kept.
 */
export function withBoardLabels(params: URLSearchParams, labelIds: readonly string[]) {
  const next = new URLSearchParams(params)
  next.delete(labelParam)
  for (const id of labelIds) next.append(labelParam, id)
  return next
}

/**
 * A Board's URL with every filter cleared, its Labels and the text search too, and its view
 * options kept.
 */
export function withoutBoardFilters(params: URLSearchParams) {
  const next = new URLSearchParams(params)
  for (const filter of [...boardFilters, labelParam]) next.delete(filter)
  return next
}

/** Names compared as the viewer's language sorts them, ignoring case. */
const byName = new Intl.Collator(undefined, { sensitivity: "accent" }).compare

/**
 * A filter menu's values in the order it lists them, on the Board and the Epics tab: its
 * `nothing` value first (Mine, No Area), then the rest by name, alphabetically, ignoring case, as
 * the viewer's language sorts them. The Assignee, Area and Label menus list their values this
 * way, whatever the Project's order; Type, Priority, Size and Epic keep the order they are given,
 * which is their natural one and, for Epics, Epic order.
 */
export function inMenuOrder<Value extends { id: string; label: string }>(
  values: readonly Value[],
  nothing?: string,
): Value[] {
  const first = values.filter(({ id }) => id === nothing)
  const rest = values.filter(({ id }) => id !== nothing)
  return [...first, ...rest.sort((a, b) => byName(a.label, b.label))]
}

/**
 * The values chosen in a facet, in the order its menu lists them, so the same Labels make the same
 * chip whatever order they were chosen in. One the menu no longer lists, as from an old link,
 * comes after those it does.
 */
export function chosenInMenuOrder(chosen: readonly string[], values: readonly { id: string }[]) {
  const listed = values.map(({ id }) => id).filter((id) => chosen.includes(id))
  return [...listed, ...chosen.filter((id) => !listed.includes(id))]
}

/** The order a Board's filter chips stand in, by their facet's field. */
const chipFields = ["assignee", "type", "priority", "size", "epic", "area", "label"]

/**
 * Facets in the field order their chips stand in (Assignee, Type, Priority, Size, Epic, Area,
 * Label), whatever order they were applied in, so the same filters always look the same.
 */
export function inChipOrder<Facet extends { id: string }>(facets: readonly Facet[]): Facet[] {
  const at = ({ id }: Facet) =>
    chipFields.includes(id) ? chipFields.indexOf(id) : chipFields.length
  return [...facets].sort((a, b) => at(a) - at(b))
}

/**
 * How a Board's cards are laid out: in columns alone, in a lane per Epic or per Area, or in a lane
 * per Area holding a lane per Epic.
 */
export type BoardGrouping = "none" | "epic" | "area" | "area-epic"

const groupings: readonly BoardGrouping[] = ["none", "epic", "area", "area-epic"]

/**
 * How a Board is laid out and what it includes beyond its filters, as its Display menu sets them:
 * its grouping, completed Epics left out (which only a Board whose lanes hold Epics does), and
 * cards that closed more than 14 days ago put back.
 */
export type BoardDisplay = { grouping: BoardGrouping; hidingCompleted: boolean; older: boolean }

/** The URL parameter each switch in the Display menu is kept in, and the value it has while on. */
const switchParams = {
  hidingCompleted: ["completed", "hide"],
  older: ["older", "true"],
} as const satisfies Record<Exclude<keyof BoardDisplay, "grouping">, readonly [string, string]>

/**
 * A Board's Display options as its URL keeps them: a grouping as `group=epic`, `group=area` or
 * `group=area-epic`.
 */
export function boardDisplayIn(params: URLSearchParams): BoardDisplay {
  const on = ([name, value]: readonly [string, string]) => params.get(name) === value
  const group = params.get("group")
  return {
    grouping: groupings.find((grouping) => grouping === group) ?? "none",
    hidingCompleted: on(switchParams.hidingCompleted),
    older: on(switchParams.older),
  }
}

/**
 * A Board's URL with Display options changed, each dropped from it while off (no grouping too), and
 * the rest kept.
 */
export function withBoardDisplay(params: URLSearchParams, change: Partial<BoardDisplay>) {
  const next = new URLSearchParams(params)
  const { grouping, ...switches } = change
  if (grouping === "none") next.delete("group")
  else if (grouping) next.set("group", grouping)
  for (const [option, on] of Object.entries(switches) as [keyof typeof switchParams, boolean][]) {
    const [name, value] = switchParams[option]
    if (on) next.set(name, value)
    else next.delete(name)
  }
  return next
}

/** The URL parameters a Board's Display options are kept in. */
const displayParams = ["group", ...Object.values(switchParams).map(([name]) => name)]

/*
 * Views. A Board's URL names the View it has loaded as `view=<id>`, beside the filters, search and
 * Display it shows, which loading the View wrote there. While a View is loaded the Status columns
 * it collapses ride in the URL too, as a repeated `collapsed`; on All tickets (no `view`) they are
 * kept in the viewer's browser instead. The phone's `column`, the peeked `ticket` and a composer's
 * `new` are no part of a View, and loading one leaves them as they are.
 */

/** The URL parameter naming the View a Board has loaded, by its id. */
export const viewParam = "view"

/** The parameter a Board's URL repeats, once for each column collapsed while a View is loaded. */
const collapsedParam = "collapsed"

/** Every URL parameter a View fills, which loading one, or All tickets, replaces. */
const viewedParams = [viewParam, ...boardFilters, labelParam, ...displayParams, collapsedParam]

/** The View a Board's URL has loaded, by its id; none for All tickets. */
export const loadedViewIn = (params: URLSearchParams) => params.get(viewParam) || undefined

/**
 * Whether a Board's URL names a View and none of the settings a View saves, as a hand-trimmed link
 * does: one the page opens with the View's saved settings rather than as modified.
 */
export const isBareViewLink = (params: URLSearchParams) =>
  loadedViewIn(params) !== undefined &&
  viewedParams.every((name) => name === viewParam || !params.has(name))

/**
 * The View a Board opening from its URL reloads, of those the Member sees (`seen`), given the one
 * last loaded on its Project in this browser by id (`remembered`; none for All tickets): that View
 * when the URL is bare, naming no View and none of a View's settings; `gone` when it is no longer
 * among them, so it is forgotten and the Board stays on All tickets; and nothing when the URL says
 * what to open itself, or nothing is remembered.
 */
export function reopenedViewOf<Seen extends { id: string }>(
  params: URLSearchParams,
  remembered: string | null,
  seen: readonly Seen[],
): Seen | "gone" | undefined {
  if (!remembered || viewedParams.some((name) => params.has(name))) return undefined
  return seen.find(({ id }) => id === remembered) ?? "gone"
}

/** The Status columns a Board's URL collapses while a View is loaded, by id, each once. */
export function collapsedColumnsIn(params: URLSearchParams) {
  return [...new Set(params.getAll(collapsedParam).filter(Boolean))]
}

/** A Board's URL with Status columns collapsed, or opened, and the rest kept. */
export function withCollapsedColumns(
  params: URLSearchParams,
  changed: string | readonly string[],
  collapsed: boolean,
) {
  const ids = new Set(collapsedColumnsIn(params))
  for (const id of typeof changed === "string" ? [changed] : changed)
    if (collapsed) ids.add(id)
    else ids.delete(id)
  const next = new URLSearchParams(params)
  next.delete(collapsedParam)
  for (const id of ids) next.append(collapsedParam, id)
  return next
}

/**
 * What a View saves of the Board its URL shows: the filters, search and Labels, the Display, and
 * `collapsed`, the Status columns collapsed, which the page reads from the URL while a View is
 * loaded and from the browser on All tickets.
 */
export function viewSettingsIn(
  params: URLSearchParams,
  collapsed: readonly string[],
): ViewSettings {
  const { type, priority, ...filters } = boardFiltersIn(params)
  const labels = boardLabelsIn(params)
  const { grouping, hidingCompleted, older } = boardDisplayIn(params)
  return {
    filters: {
      ...filters,
      // As the URL says them: `projects` checks each when the View is saved.
      ...(type && { type: type as ViewFilters["type"] }),
      ...(priority && { priority: priority as ViewFilters["priority"] }),
      ...(labels.length > 0 && { labels }),
    },
    display: { group: grouping, completed: hidingCompleted ? "hide" : "show", older },
    collapsedStatuses: [...collapsed],
  }
}

type ViewFilters = ViewSettings["filters"]

/**
 * A Board's URL with every parameter a View fills dropped: All tickets, with no filter and the
 * default Display, its columns collapsed as the browser keeps them. The phone's column, the peeked
 * ticket and a composer stay.
 */
export function withAllTickets(params: URLSearchParams) {
  const next = new URLSearchParams(params)
  for (const name of viewedParams) next.delete(name)
  return next
}

/**
 * A Board's URL with its View unloaded but its filters, search and Display kept in force, unsaved,
 * as a link to a View that no longer exists shows them. Its columns go back to collapsing as the
 * browser keeps them.
 */
export function withoutView(params: URLSearchParams) {
  const next = withCollapsedColumns(params, collapsedColumnsIn(params), false)
  next.delete(viewParam)
  return next
}

/**
 * A Board's URL showing a View: `view=<id>` and every one of its settings, in place of whatever
 * the URL had of another View or none. The phone's column, the peeked ticket and a composer stay.
 */
export function withView(
  params: URLSearchParams,
  { id, settings }: { id: string; settings: ViewSettings },
) {
  const { labels = [], ...filters } = settings.filters
  let next = withAllTickets(params)
  next.set(viewParam, id)
  for (const filter of boardFilters) next = withBoardFilter(next, filter, filters[filter])
  next = withBoardLabels(next, labels)
  next = withBoardDisplay(next, {
    grouping: settings.display.group,
    hidingCompleted: settings.display.completed === "hide",
    older: settings.display.older,
  })
  return withCollapsedColumns(next, settings.collapsedStatuses, true)
}

/**
 * What a Project has now that a View may name, by id: its Areas, Labels and Status columns, and
 * its Epics by number, completed ones too. `epics` is none while they aren't all known, as on a
 * Board that leaves completed Epics out.
 */
export type ProjectValues = {
  areas: readonly string[]
  labels: readonly string[]
  statuses: readonly string[]
  epics?: readonly number[]
}

/**
 * What a Board's answer says its Project has. Its Epics are every one when the Board was asked with
 * completed Epics in (`everyEpic`); otherwise it leaves them out, and they are known only with the
 * Project's Epic list (`epicList`, completed ones included), which adds to the Board's.
 */
export function projectValuesOf(
  board: Pick<Board, "areas" | "labels" | "statuses" | "epics">,
  { everyEpic, epicList }: { everyEpic: boolean; epicList?: readonly number[] },
): ProjectValues {
  const onBoard = board.epics.map(({ number }) => number)
  const epics = everyEpic ? onBoard : epicList && [...new Set([...onBoard, ...epicList])]
  return {
    areas: board.areas.map(({ id }) => id),
    labels: board.labels.map(({ id }) => id),
    statuses: board.statuses.map(({ id }) => id),
    ...(epics && { epics }),
  }
}

/**
 * A View's settings as the Project has them now: an Area, an Epic, Labels and collapsed columns it
 * no longer has dropped, a Label filter left with none dropped altogether. No Area and No Epic
 * stay, and so does any Epic while `has` doesn't know them all. A removed value is dropped when a
 * View is applied and before it is compared, so one deletion never breaks or modifies a View.
 */
export function resolvedSettings(settings: ViewSettings, has: ProjectValues): ViewSettings {
  const { area, epic, labels, ...filters } = settings.filters
  const kept = labels?.filter((id) => has.labels.includes(id)) ?? []
  const epicHeld = epic === "none" || !has.epics || has.epics.includes(Number(epic))
  return {
    filters: {
      ...filters,
      ...(area !== undefined && (area === "none" || has.areas.includes(area)) && { area }),
      ...(epic !== undefined && epicHeld && { epic }),
      ...(kept.length > 0 && { labels: kept }),
    },
    display: settings.display,
    collapsedStatuses: settings.collapsedStatuses.filter((id) => has.statuses.includes(id)),
  }
}

/**
 * A View's link without the values its Project no longer has, as the Board should show it: the
 * Area, Epic, Labels and collapsed columns `resolvedSettings` drops, dropped from the URL, and the
 * rest kept. None when the link names no View, or nothing the Project has lost.
 */
export function withoutRemovedValues(params: URLSearchParams, has: ProjectValues) {
  const id = loadedViewIn(params)
  if (!id) return undefined
  const settings = viewSettingsIn(params, collapsedColumnsIn(params))
  const shown = withView(params, { id, settings })
  const cleaned = withView(params, { id, settings: resolvedSettings(settings, has) })
  return cleaned.toString() === shown.toString() ? undefined : cleaned
}

/** Settings as they compare: resolved, the search trimmed, Labels and columns as sorted sets. */
function comparedAs(settings: ViewSettings, has: ProjectValues) {
  const { filters, display, collapsedStatuses } = resolvedSettings(settings, has)
  const { labels = [], text, ...rest } = filters
  const shown = Object.entries({ ...rest, text: text?.trim() })
    .filter(([, value]) => value)
    .sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify([
    shown,
    [...new Set(labels)].sort(),
    [display.group, display.completed, display.older],
    [...new Set(collapsedStatuses)].sort(),
  ])
}

/**
 * Whether a loaded View is modified: the settings its Board shows differ from those it saved, both
 * read the same way. Values the Project no longer has are dropped from both sides first, and
 * Labels and collapsed columns compare as sets, so their order never counts.
 */
export const isViewModified = (saved: ViewSettings, shown: ViewSettings, has: ProjectValues) =>
  comparedAs(saved, has) !== comparedAs(shown, has)

/**
 * The grouping a Board is shown in: the one its URL asks for, but on a Project with no Areas none
 * for a grouping by Area and Epic alone for one by Area then Epic, so a link kept from before its
 * Areas went still shows the Board.
 */
export function shownGrouping(grouping: BoardGrouping, areas: readonly Area[]): BoardGrouping {
  if (areas.length > 0) return grouping
  return grouping === "area" ? "none" : grouping === "area-epic" ? "epic" : grouping
}

/** Whether a grouping's lanes hold Epics, so leaving completed Epics out changes the Board. */
export const nestsEpics = (grouping: BoardGrouping) =>
  grouping === "epic" || grouping === "area-epic"

/**
 * Whether the Board is asked for without completed Epics and their cards: only while its lanes
 * hold Epics, and the Display menu turned them off.
 */
export const leavesOutCompletedEpics = ({ grouping, hidingCompleted }: BoardDisplay) =>
  hidingCompleted && nestsEpics(grouping)

/**
 * Whether a Board's Display differs from the default in what it shows, which its Display button
 * marks. Leaving completed Epics out counts only while the lanes hold Epics, the one layout it
 * changes, and grouping marks the button anyway; otherwise `completed=hide` stays in the URL for
 * when the Board is grouped by Epic again, and the button doesn't claim a change it doesn't make.
 */
export const isDisplayChanged = ({ grouping, older }: BoardDisplay) => grouping !== "none" || older

/**
 * Whether any filter is in force on a Board: one its URL names once, or a Label it is filtered
 * to. `labelIds` are the Project's own among those the URL names, since `projects` leaves any other
 * out of the filter.
 */
export const isBoardFiltered = (
  filters: Partial<Record<BoardFilter, string>>,
  labelIds: readonly string[],
) => Object.keys(filters).length > 0 || labelIds.length > 0

/**
 * Why a Board has no card to show, if it has none: `filtered` when filters are in force, so
 * clearing them may bring cards back; `new` for a Project with nothing in it yet, not even an
 * Epic. A Project with Epics but no card keeps its empty columns, ready for the first.
 *
 * A Board leaves off cards that closed more than 14 days ago, so a Project whose every ticket did
 * reads as new too; the Board can't tell the two apart without asking for them all, and the
 * Display menu puts them back.
 */
export function emptinessOf(
  board: Pick<Board, "tickets" | "epics">,
  filtered: boolean,
): "filtered" | "new" | undefined {
  if (board.tickets.length > 0) return undefined
  if (filtered) return "filtered"
  return board.epics.length === 0 ? "new" : undefined
}
